import React, { useState } from 'react'
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Link,
  CircularProgress,
  makeStyles
} from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import axios from 'axios'

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  logo: {
    maxHeight: 80,
    marginBottom: theme.spacing(2),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  nameRow: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  consentBox: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2.5),
    marginTop: theme.spacing(2),
  },
  checkboxLabel: {
    alignItems: 'flex-start',
    marginLeft: 0,
  },
  checkbox: {
    paddingTop: 2,
  },
  disclosureList: {
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    marginBottom: 0,
    '& li': {
      marginBottom: theme.spacing(0.5),
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
    },
  },
  submitBtn: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5),
  },
  footer: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  successContainer: {
    textAlign: 'center',
    padding: theme.spacing(6, 2),
  },
  successIcon: {
    fontSize: 64,
    color: theme.palette.success.main || '#4caf50',
    marginBottom: theme.spacing(2),
  },
}))

const PRIVACY_POLICY_URL = '/online/app-privacy-policy'
const COMPANY_NAME = 'Mariani Premier Group'
const COMPANY_WEBSITE = 'www.hmiofaz.com'

const SMSConsentPage = () => {
  const classes = useStyles()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [consentChecked, setConsentChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value)
    setPhone(formatted)
  }

  const isValid = firstName.trim() && lastName.trim() && phone.replace(/\D/g, '').length === 10 && consentChecked

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isValid) return

    setSubmitting(true)
    setError('')

    try {
      await axios.post('/api/sms-consent', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.replace(/\D/g, ''),
        consentedAt: new Date().toISOString(),
      })
      setSubmitted(true)
    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <Container className={classes.root} maxWidth="sm" component={Paper} elevation={2}>
        <Box p={4} className={classes.successContainer}>
          <CheckCircleOutlineIcon className={classes.successIcon} />
          <Typography variant="h5" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" color="textSecondary">
            You have successfully opted in to receive SMS messages from {COMPANY_NAME}.
            You can opt out at any time by texting STOP.
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container className={classes.root} maxWidth="sm" component={Paper} elevation={2}>
      <Box p={4}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography variant="h4" component="h1" gutterBottom>
            SMS Notifications
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Sign up to receive SMS notifications from {COMPANY_NAME}.
          </Typography>
        </Box>

        <Divider />

        {/* Form */}
        <form onSubmit={handleSubmit} className={classes.section}>
          <Box className={classes.form}>
            <Box className={classes.nameRow}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Box>

            <TextField
              label="Phone Number"
              variant="outlined"
              fullWidth
              required
              value={phone}
              onChange={handlePhoneChange}
              placeholder="(555) 555-5555"
              inputProps={{ maxLength: 14 }}
            />

            {/* SMS Consent Checkbox with Required Disclosures */}
            <Box className={classes.consentBox}>
              <FormControlLabel
                className={classes.checkboxLabel}
                control={
                  <Checkbox
                    className={classes.checkbox}
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to receive SMS messages from {COMPANY_NAME}. I understand that:
                  </Typography>
                }
              />
              <ul className={classes.disclosureList}>
                <li>Messaging frequency may vary.</li>
                <li>Message and data rates may apply.</li>
                <li>You can opt out any time by texting <strong>STOP</strong>.</li>
                <li>For assistance, text <strong>HELP</strong> or visit our website at{' '}
                  <Link href={`https://${COMPANY_WEBSITE}`} className={classes.link} target="_blank" rel="noopener noreferrer">
                    {COMPANY_WEBSITE}
                  </Link>.
                </li>
                <li>
                  View our{' '}
                  <Link href={PRIVACY_POLICY_URL} className={classes.link} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link href={`${PRIVACY_POLICY_URL}#request`} className={classes.link} target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </Link>.
                </li>
              </ul>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                We do not share or sell your SMS opt-in data or phone numbers to third parties for marketing purposes.
              </Typography>
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              className={classes.submitBtn}
              disabled={!isValid || submitting}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'I Agree & Sign Up'}
            </Button>
          </Box>
        </form>

        {/* Footer */}
        <Box className={classes.footer}>
          <Divider />
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              &copy; {new Date().getFullYear()} Mariani Enterprises LLC (dba HMI of AZ). All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  )
}

export default SMSConsentPage
